import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, useHistory, useLocation, useRouteMatch } from "react-router";

import { cn } from "@btnet/utilities-core";

import { BdsIconMagnifyingGlass, BdsText } from "@buildertrend/components";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { useTracking } from "utilities/analytics/analytics";
import { routes } from "utilities/routes";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTIconLoadingOutlined, BTIconMagnifyingGlassX } from "commonComponents/btWrappers/BTIcon";
import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";
import { SearchBarRoutes } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBarRoutes";

import { FeedbackTarget } from "handlers";

import "./AiSearch.less";

import { AiSearchQA } from "./AiSearchQA";
import {
    AiSearchResults,
    EXCLUDED_ENTITIES,
    getEntityLink,
    navigateEntityLink,
    OPTION_ID_PREFIX,
} from "./AiSearchResults";
import { getHitKey, normalizeKey } from "./entityMeta";
import { useAiSearch } from "./hooks/useAiSearch";
import type { AiSearchPhase, ICategorizedHit } from "./types";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SKELETON_COUNT = 4;
/**
 * Cap on the number of ranked results serialized into the "Search" telemetry
 * event. Keeps the payload well under Sawmill's per-event size limit while still
 * capturing the full visible result set for relevance analysis.
 */
const MAX_LOGGED_RESULTS = 50;

interface IAiSearchProps {
    handleHideSearchInput?: () => void;
    onFocus?: () => void;
    isSmallWidth: boolean;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent.
     */
    builderInfo?: BuilderInfo | null;
}

function SkeletonRows() {
    return (
        <div className="ai-search-skeleton-list">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <div className="ai-search-skeleton" key={i}>
                    <div className="ai-search-skeleton-icon" />
                    <div className="ai-search-skeleton-lines">
                        <div className="ai-search-skeleton-line ai-search-skeleton-line--medium" />
                        <div className="ai-search-skeleton-line ai-search-skeleton-line--short" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function AiSearch(props: IAiSearchProps) {
    const { handleHideSearchInput, onFocus, isSmallWidth, builderInfo } = props;
    const { doSearch, cancel, state } = useAiSearch();
    const { trackEvent } = useTracking({ component: "AI Search" });
    const history = useHistory();
    const match = useRouteMatch();
    const location = useLocation();
    const spaContext = useContext(SpaInfoContext);

    const [query, setQuery] = useState("");
    const [panelOpen, setPanelOpen] = useState(isSmallWidth);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [dropdownLeft, setDropdownLeft] = useState<number | undefined>(undefined);
    const lastTrackedPhaseRef = useRef<string | null>(null);

    // Snapshot of the most recent search's outcome, kept in a ref so abandonment
    // can be reported from unmount/close/navigation handlers without stale closures.
    const searchOutcomeRef = useRef<{
        searchId: string;
        phase: AiSearchPhase;
        query: string;
        resultCount: number;
        clicked: boolean;
    }>({ searchId: "", phase: "idle", query: "", resultCount: 0, clicked: false });
    // searchIds already reported as abandoned, so close + unmount + next-search
    // can't emit duplicate abandonment events for the same search.
    const abandonReportedRef = useRef<Set<string>>(new Set());

    const results = useMemo(
        () =>
            state.results.filter(
                (h) => !EXCLUDED_ENTITIES.has(normalizeKey(h.entity)) && getEntityLink(h) !== null,
            ),
        [state.results],
    );
    const staleResults = useMemo(
        () =>
            state.staleResults.filter(
                (h) => !EXCLUDED_ENTITIES.has(normalizeKey(h.entity)) && getEntityLink(h) !== null,
            ),
        [state.staleResults],
    );

    const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
    const hasResults = results.length > 0;
    const hasStaleResults = staleResults.length > 0;
    const hasQA = Object.keys(state.qaAnswers).length > 0;
    const showPanel = panelOpen;
    const showInstruction = panelOpen && !hasQuery && state.phase === "idle";
    const showSkeleton = hasQuery && state.phase === "loading" && !hasStaleResults;
    const showStaleResults = hasQuery && state.phase === "loading" && hasStaleResults;
    const showResults = hasQuery && hasResults;
    const showEmpty = state.phase === "empty";
    const resultCount = results.length;

    // Emit an "abandoned" search event when a completed search (results shown or
    // empty) is left without the user clicking a result. Guarded so the same
    // searchId is reported at most once across the close/unmount/next-search paths.
    const emitAbandon = useCallback(
        (snap: typeof searchOutcomeRef.current) => {
            if (!snap.searchId || snap.clicked) return;
            if (snap.phase !== "done" && snap.phase !== "empty") return;
            if (abandonReportedRef.current.has(snap.searchId)) return;
            abandonReportedRef.current.add(snap.searchId);
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: snap.searchId,
                    query: snap.query,
                    resultCount: snap.resultCount,
                    searchPhase: "abandoned",
                },
            });
        },
        [trackEvent],
    );

    // Hold the latest emitAbandon so the unmount-only cleanup can call it without
    // depending on its identity. Listing emitAbandon as a dependency there would
    // make the cleanup fire whenever its identity changes (e.g. if trackEvent is
    // recreated), emitting a spurious abandonment while the user is still viewing
    // results.
    const emitAbandonRef = useRef(emitAbandon);
    useEffect(() => {
        emitAbandonRef.current = emitAbandon;
    }, [emitAbandon]);

    // Keep searchOutcomeRef current, and report abandonment when one search is
    // superseded by another (query reformulation) without an intervening click.
    // Uses state.searchQuery (the query that triggered the search), not the live
    // input, so a deferred abandonment event reports the original query rather
    // than whatever the user has since typed.
    useEffect(() => {
        const prev = searchOutcomeRef.current;
        if (prev.searchId && prev.searchId !== state.searchId) {
            emitAbandon(prev);
        }
        searchOutcomeRef.current = {
            searchId: state.searchId,
            phase: state.phase,
            query: state.searchQuery,
            resultCount,
            clicked: prev.searchId === state.searchId ? prev.clicked : false,
        };
    }, [state.searchId, state.phase, state.searchQuery, resultCount, emitAbandon]);

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value);
            setPanelOpen(true);
            setHighlightedIndex(-1);
            // Cancel any pending post-click close — otherwise the 150ms timer
            // from handleResultClick wipes the user's newly-typed query.
            setPendingKey(null);
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (value.trim().length < MIN_QUERY_LENGTH) {
                // Clearing/shortening below the threshold abandons the current
                // completed search just like a close or navigation — emit before
                // cancel() flips phase to "idle" and disqualifies the snapshot.
                // emitAbandon's done/empty guard ignores in-flight cancellations
                // and the dedup set prevents a double-emit if a close/nav follows.
                emitAbandon(searchOutcomeRef.current);
                cancel();
                return;
            }

            debounceRef.current = setTimeout(() => {
                doSearch(value.trim());
            }, DEBOUNCE_MS);
        },
        [doSearch, cancel, emitAbandon],
    );

    const handleClose = useCallback(() => {
        emitAbandon(searchOutcomeRef.current);
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setPanelOpen(false);
        setQuery("");
        setHighlightedIndex(-1);
        setPendingKey(null);
        cancel();
        handleHideSearchInput?.();
    }, [cancel, handleHideSearchInput, emitAbandon]);

    // Clear query/panel state on route navigation so input content doesn't
    // leak forward across back/forward or sidebar nav. Skip our own /SearchResult/
    // pushes (triggered by handleResultClick → navigateEntityLink).
    const previousPathnameRef = useRef(location.pathname);
    useEffect(() => {
        if (location.pathname === previousPathnameRef.current) return;
        const isSelfPushToModal = location.pathname.startsWith(
            previousPathnameRef.current.replace(/\/$/, "") + routes.search.resultModalPrefix + "/",
        );
        previousPathnameRef.current = location.pathname;
        if (isSelfPushToModal) return;
        // Navigating elsewhere (sidebar, back/forward) without clicking a result
        // is an abandonment of the current search.
        emitAbandon(searchOutcomeRef.current);
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setQuery("");
        setHighlightedIndex(-1);
        setPendingKey(null);
        setPanelOpen(isSmallWidth);
        cancel();
    }, [location.pathname, isSmallWidth, cancel, emitAbandon]);

    const handleFocus = useCallback(() => {
        setPanelOpen(true);
        onFocus?.();
    }, [onFocus]);

    const trackSearch = useCallback(
        (searchPhase: "first-results" | "complete" | "interrupted") => {
            const durationMs =
                state.searchStartTime != null ? Date.now() - state.searchStartTime : undefined;
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: state.searchId,
                    // The query that triggered this search, not the live input —
                    // consistent with the abandonment path, so a "complete" event
                    // can't log a newly-typed query against the old search's id.
                    query: state.searchQuery,
                    resultCount,
                    // Total hits returned before client-side filtering (excluded
                    // entities + unroutable hits). resultCount is the visible count.
                    rawResultCount: state.results.length,
                    searchPhase,
                    durationMs,
                    // Parsed intent (entity targeting + question detection).
                    entityScope: state.entityScope,
                    scopedEntities: [...state.scopedEntities],
                    isQuestion: state.isQuestion,
                    // The full ranked result list the user is shown. Titles are
                    // intentionally omitted — summaries can contain customer PII;
                    // ids + scores are enough to reconstruct ranking quality.
                    results: results.slice(0, MAX_LOGGED_RESULTS).map((hit, i) => ({
                        rank: i + 1,
                        entity: normalizeKey(hit.entity),
                        id: hit.id,
                        score: hit.score,
                        rerankScore: hit.rerank_score,
                        reranker: hit.reranker,
                        modalities: [...new Set(hit.sourceOps.map((op) => op.modality))],
                    })),
                    // Per-operation breakdown: which sub-queries ran, their modality,
                    // how many hits each returned, and how long each took.
                    operations: state.operations.map((op) => ({
                        id: op.id,
                        entity: op.entity,
                        modality: op.modality,
                        stage: op.stage,
                        status: op.status,
                        resultCount: op.resultCount,
                        elapsedMs: op.elapsed_ms,
                    })),
                    qaAnswerSources: Object.keys(state.qaAnswers),
                },
            });
        },
        [
            trackEvent,
            resultCount,
            results,
            state.searchId,
            state.searchQuery,
            state.searchStartTime,
            state.results.length,
            state.entityScope,
            state.scopedEntities,
            state.isQuestion,
            state.operations,
            state.qaAnswers,
        ],
    );

    // lastTrackedPhaseRef prevents duplicate events when trackSearch changes
    useEffect(() => {
        if (state.phase === "streaming" && lastTrackedPhaseRef.current !== "streaming") {
            lastTrackedPhaseRef.current = "streaming";
            trackSearch("first-results");
        } else if (
            (state.phase === "done" || state.phase === "empty") &&
            lastTrackedPhaseRef.current !== state.phase
        ) {
            lastTrackedPhaseRef.current = state.phase;
            trackSearch("complete");
        } else if (state.phase === "loading" || state.phase === "idle") {
            lastTrackedPhaseRef.current = null;
        }
    }, [state.phase, trackSearch]);

    const handleResultClick = useCallback(
        (hit: ICategorizedHit, index: number, navMethod: "click" | "keyboard" = "click") => {
            if (state.phase !== "done" && state.phase !== "empty") {
                trackSearch("interrupted");
            }
            // Mark this search as having a click so it isn't later counted as abandoned.
            searchOutcomeRef.current.clicked = true;
            trackEvent({
                event: "LinkClick",
                element: "Link",
                uniqueId: `${normalizeKey(hit.entity)}`,
                extraInfo: {
                    searchId: state.searchId,
                    searchQuery: query,
                    searchResultRank: index + 1,
                    searchResultId: hit.id,
                    searchResultTitle: hit.summary,
                    searchResultType: normalizeKey(hit.entity),
                    searchResultScore: hit.score,
                    searchResultRerankScore: hit.rerank_score,
                    resultCount,
                    timeToClickMs:
                        state.searchStartTime != null
                            ? Date.now() - state.searchStartTime
                            : undefined,
                    navMethod,
                },
            });

            // Show a brief pending acknowledgement on the clicked row so the
            // user sees the click registered even though the entity modal takes
            // 1-2s to mount.
            setPendingKey(getHitKey(hit));
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                handleClose();
            }, 150);
        },
        [
            trackSearch,
            trackEvent,
            query,
            handleClose,
            state.phase,
            state.searchId,
            state.searchStartTime,
            resultCount,
        ],
    );

    // Focus input when panel opens at small widths (input transitions from hidden to visible)
    useEffect(() => {
        if (panelOpen && isSmallWidth && inputRef.current) {
            inputRef.current.focus();
        }
    }, [panelOpen, isSmallWidth]);

    // Left-align dropdown with input at wide widths, updating on resize
    useEffect(() => {
        if (!panelOpen || isSmallWidth || !inputRef.current) {
            setDropdownLeft(undefined);
            return;
        }

        const input = inputRef.current;
        const sync = () => setDropdownLeft(input.getBoundingClientRect().left);
        sync();

        const ro = new ResizeObserver(sync);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [panelOpen, isSmallWidth]);

    // Navigate to highlighted result on Enter
    const navigateToHighlighted = useCallback(() => {
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
            const hit = results[highlightedIndex];
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) return;

            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
            handleResultClick(hit, highlightedIndex, "keyboard");
        }
    }, [
        highlightedIndex,
        results,
        handleResultClick,
        history,
        match.url,
        spaContext.isSpa,
        builderInfo,
    ]);

    // Keyboard navigation
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const count = results.length;

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    if (count === 0) return;
                    setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    if (count === 0) return;
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
                    break;
                }
                case "Enter": {
                    if (highlightedIndex >= 0 && highlightedIndex < count) {
                        e.preventDefault();
                        navigateToHighlighted();
                    }
                    break;
                }
                case "Escape": {
                    e.preventDefault();
                    handleClose();
                    break;
                }
            }
        },
        [results.length, highlightedIndex, navigateToHighlighted, handleClose],
    );

    // Scroll highlighted result into view
    useEffect(() => {
        if (highlightedIndex < 0) return;
        const el = document.getElementById(`${OPTION_ID_PREFIX}${highlightedIndex}`);
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    // Reset highlight when results change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [resultCount]);

    // Close on outside click
    useEffect(() => {
        if (!panelOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (isFeedbackOpen) return;
            const target = e.target as Node;
            if (
                panelRef.current &&
                !panelRef.current.contains(target) &&
                inputRef.current &&
                !inputRef.current.contains(target)
            ) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [panelOpen, handleClose, isFeedbackOpen]);

    // Emit a final abandonment and abort in-flight requests on unmount ONLY.
    // Empty deps + reading emitAbandon through a ref keep this from firing
    // mid-session when a dependency's identity changes. cancel is stable
    // (useCallback with empty deps in the hook).
    useEffect(() => {
        return () => {
            emitAbandonRef.current(searchOutcomeRef.current);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeDescendant =
        highlightedIndex >= 0 ? `${OPTION_ID_PREFIX}${highlightedIndex}` : undefined;

    const resultCountText = resultCount === 1 ? "1 result" : `${resultCount} results`;

    const feedbackExtraInfo = JSON.stringify({
        searchId: state.searchId,
        query,
        resultCount,
        topResults: results.slice(0, 10).map((r) => ({
            entity: r.entity,
            id: r.id,
            summary: r.summary,
        })),
    });

    return (
        <div className="ai-search-wrapper">
            <div className="ai-search-input-row">
                {/* Native input required: BTInput doesn't support combobox
                    role, aria-activedescendant, or aria-expanded */}
                {/* eslint-disable-next-line react/forbid-elements */}
                <input
                    ref={inputRef}
                    className="ai-search-overlay-input"
                    type="text"
                    placeholder={isSmallWidth ? "Search..." : "Search or ask a question..."}
                    value={query}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    role="combobox"
                    aria-expanded={hasQuery}
                    aria-controls="ai-search-listbox"
                    aria-activedescendant={activeDescendant}
                    aria-autocomplete="list"
                    data-testid="ai-search-input"
                />
            </div>

            {showPanel && (
                <>
                    <div
                        className={cn("ai-search-backdrop", {
                            "ai-search-backdrop--behind-modal": isFeedbackOpen,
                        })}
                        onClick={() => {
                            if (!isFeedbackOpen) handleClose();
                        }}
                        data-testid="ai-search-overlay"
                    />
                    <div
                        ref={panelRef}
                        className={cn("ai-search-dropdown", {
                            "ai-search-dropdown--mobile": isSmallWidth,
                            "ai-search-dropdown--behind-modal": isFeedbackOpen,
                        })}
                        style={
                            !isSmallWidth && dropdownLeft !== undefined
                                ? { left: dropdownLeft }
                                : undefined
                        }
                        data-testid="ai-search-panel"
                    >
                        {/* Progress bar during streaming */}
                        {(state.phase === "loading" || state.phase === "streaming") && (
                            <div className="ai-search-progress">
                                <div className="ai-search-progress-bar" />
                            </div>
                        )}

                        {/* Instruction: no query yet */}
                        {showInstruction && (
                            <div className="ai-search-instruction">
                                <div className="ai-search-instruction-icon">
                                    <BdsIconMagnifyingGlass />
                                </div>
                                <h3>Find anything in Buildertrend</h3>
                                <p>Search across all your jobs.</p>
                            </div>
                        )}

                        {/* Skeleton loading (first search only, no prior results) */}
                        {showSkeleton && <SkeletonRows />}

                        {/* Stale results — faded previous results while new search loads */}
                        {showStaleResults && (
                            <div className="ai-search-results-scroll ai-search-results-scroll--stale">
                                <AiSearchResults
                                    results={staleResults}
                                    query={query}
                                    highlightedIndex={-1}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* Error */}
                        {state.error && <div className="ai-search-error">{state.error}</div>}

                        {/* QA answer */}
                        {hasQA && <AiSearchQA qaAnswers={state.qaAnswers} />}

                        {/* Result count header */}
                        {showResults && (
                            <div
                                className="ai-search-count-header"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                <span>
                                    {state.phase === "streaming" && (
                                        <BTIconLoadingOutlined
                                            spin
                                            size={12}
                                            className="ai-search-streaming-spinner"
                                        />
                                    )}
                                    {state.phase === "streaming"
                                        ? `${resultCountText} so far...`
                                        : resultCountText}
                                </span>
                                <BTButton
                                    type="tertiary"
                                    onClick={() => setIsFeedbackOpen(true)}
                                    data-testid="ai-search-feedback-link"
                                    className="ai-search-feedback-link"
                                >
                                    Not finding it? Tell us
                                </BTButton>
                            </div>
                        )}

                        {isFeedbackOpen && (
                            <FeedbackWidget
                                feedbackTarget={FeedbackTarget.AiSearch}
                                onClose={() => setIsFeedbackOpen(false)}
                                extraInfo={feedbackExtraInfo}
                            />
                        )}

                        {/* Scrollable results */}
                        {showResults && (
                            <div className="ai-search-results-scroll" ref={resultsRef}>
                                <AiSearchResults
                                    results={results}
                                    query={query}
                                    highlightedIndex={highlightedIndex}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* Empty state */}
                        {showEmpty && (
                            <div className="ai-search-empty" aria-live="polite">
                                <div className="ai-search-empty-icon" aria-hidden="true">
                                    <BTIconMagnifyingGlassX size={32} />
                                </div>
                                <BdsText
                                    size="distinct-sm"
                                    tag="p"
                                    className="ai-search-empty-title"
                                >
                                    No results for &ldquo;{query}&rdquo;
                                </BdsText>
                                <BdsText size="normal-sm" tag="p" className="ai-search-empty-hint">
                                    Check your spelling or try broader terms.
                                </BdsText>
                            </div>
                        )}

                        {/* Footer with keyboard hints */}
                        {showResults && (
                            <div className="ai-search-footer">
                                <div className="ai-search-shortcuts">
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&uarr;</kbd>
                                        <kbd>&darr;</kbd>
                                        navigate
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&crarr;</kbd>
                                        select
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>esc</kbd>
                                        close
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            {/* SearchBarRoutes must stay mounted after the panel closes so the
                pushed /SearchResult/* route can render the entity modal */}
            <Route render={(routeProps) => <SearchBarRoutes {...routeProps} />} />
        </div>
    );
}
